import React, { useMemo } from "react";
import { Form, theme } from "antd";
import { useTranslate } from "@refinedev/core";
import { Spinner } from "@libertify/ui";
import { PlaybookAddOnCard } from "../../PlaybookAddOnCard";
import {
  useAddonDraft,
  usePlaybookDraft,
} from "../../../context/PlaybookDraftContext";
import { useGeneralConfigData } from "../hooks/useGeneralConfigData";
import type { GeneralSettingsFormValues } from "../types";
import { GENERAL_SETTINGS_DEFAULTS } from "../defaults";
import { ExperienceDetailsSection } from "./ExperienceDetailsSection";
import { HeaderLinksSection } from "./HeaderLinksSection";
import { LayoutSection } from "./LayoutSection";
import { CHAT_ADDON_ID, CONFIG_ITEM } from "../../../data/addons";
import { SectionDivider } from "./GeneralSettingsConfigCard.styles";

const KEY = "createExperience.generalSettings";

/**
 * "General settings" configuration card (Step 2 customize view): the
 * experience's identity (name, language, tags, description), its header CTAs
 * ("Header links"), and the reading layout (side-panel position + chat).
 * Values persist in the shared PlaybookDraft context (via `useAddonDraft`, which
 * routes "general" to its own slot), so they survive switching between cards.
 *
 * All three sections are shared with the edit card (`GeneralSettingsEditCard`)
 * so both render the exact same fields — only the data source (draft context
 * vs. the real project) differs per card.
 */
export const GeneralSettingsConfigCard: React.FC = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const [form] = Form.useForm<GeneralSettingsFormValues>();
  // Org-preference defaults (brand kit / voice) under the static card
  // defaults, so an untouched card still shows the org's own entities. They
  // also ride at create time regardless of the form (see `useGeneralSeed`).
  const { defaults: orgDefaults, loadingDefaults } = useGeneralConfigData();
  const defaults = useMemo<GeneralSettingsFormValues>(
    () => ({
      ...GENERAL_SETTINGS_DEFAULTS,
      generation_settings: {
        ...GENERAL_SETTINGS_DEFAULTS.generation_settings,
        ...orgDefaults.generation_settings,
      },
    }),
    [orgDefaults]
  );
  const { initialValues, onValuesChange } = useAddonDraft(
    CONFIG_ITEM.General,
    defaults
  );
  const { addonObject } = usePlaybookDraft();

  // Read-only reflection of the real AI Chat add-on, not a separate control —
  // there's no `chatPanel` field of its own (see GeneralSettingsFormValues).
  const hasChatAddon = CHAT_ADDON_ID in addonObject;

  // antd reads `initialValues` only at mount — mounting the form before the
  // org-preference defaults exist would show empty brand kit / voice selects
  // for the whole session. Prefs are usually cached by the provider's seeder,
  // so this spinner rarely paints.
  if (loadingDefaults) {
    return (
      <PlaybookAddOnCard
        itemId={CONFIG_ITEM.General}
        title={t(`${KEY}.title`)}
        description={t(`${KEY}.subtitle`)}
      >
        <Spinner />
      </PlaybookAddOnCard>
    );
  }

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.General}
      title={t(`${KEY}.title`)}
      description={t(`${KEY}.subtitle`)}
    >
      <Form<GeneralSettingsFormValues>
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={onValuesChange}
      >
        <ExperienceDetailsSection includeAutoLanguage />

        <SectionDivider $token={token} />

        <HeaderLinksSection />

        <SectionDivider $token={token} />

        <LayoutSection hasChatAddon={hasChatAddon} />
      </Form>
    </PlaybookAddOnCard>
  );
};

export default GeneralSettingsConfigCard;
